// Initial Certification Stage 1 / Stage 2 mandays auto-split (Audit Platform).
// Stage 1 (Document Review) is a fixed 1 manday; the remainder goes to Stage 2
// (Main Audit). Mirrors backend InitialCertificationMandaysSplitter.
const STAGE1_FIXED_MANDAYS = 1;
const STAGE2_AUTO_PLOT_DAYS = 14;

export const splitInitialCertMandays = (totalMandays) => {
  const total = Number(totalMandays);
  if (!Number.isFinite(total) || total <= 0) return null;
  // Total <= the fixed Stage 1 allowance (1 manday) splits evenly instead -- a fixed
  // Stage1=1/Stage2=remainder split would leave Stage 2 (Main Audit) with zero mandays
  // whenever total <= 1, which is never realistic. E.g. total=1 -> 0.5/0.5.
  if (total <= STAGE1_FIXED_MANDAYS) {
    const half = total / 2;
    return { stage1Mandays: half, stage2Mandays: half };
  }
  const stage1Mandays = STAGE1_FIXED_MANDAYS;
  const stage2Mandays = total - stage1Mandays;
  return { stage1Mandays, stage2Mandays };
};

// Stage 2 auto-plots 14 days after Stage 1's date; returns '' when Stage 1 has no valid date yet.
export const stage2AutoDate = (stage1Date) => {
  const text = String(stage1Date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '';
  const parsed = new Date(`${text}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  parsed.setUTCDate(parsed.getUTCDate() + STAGE2_AUTO_PLOT_DAYS);
  return parsed.toISOString().slice(0, 10);
};

// Composes the default schedule row for a brand-new (never-saved) Initial
// Certification stage: mandays from the proposal split, date auto-plotted.
// Returns null when totalMandays is unknown — caller keeps manual entry.
export const buildInitialCertStageDefault = ({ stageKey, totalMandays, stage1Date }) => {
  const split = splitInitialCertMandays(totalMandays);
  if (!split) return null;

  const mandays = stageKey === 'stage2' ? split.stage2Mandays : split.stage1Mandays;
  const date = stageKey === 'stage2' ? stage2AutoDate(stage1Date) : (stage1Date || '');

  return { mandays: String(mandays), startDate: date, endDate: date };
};

// Resolves the mandays value shown on an Initial Certification Stage 1 / Stage 2
// summary card. Once AR/Scheduler has actually entered mandays for that stage in the
// Audit Scheduling step, that value always wins (it is the editable, authoritative
// figure). Before anything has been scheduled, falls back to the proposal-derived
// default split (Stage 1 = 1 fixed, Stage 2 = remainder) so the card shows a sensible
// suggestion instead of '-'.
export const resolveStageMandaysDisplay = ({ stageKey, totalMandays, scheduledMandays }) => {
  const scheduled = Number(scheduledMandays);
  if (Number.isFinite(scheduled) && scheduled > 0) return String(scheduled);

  const split = splitInitialCertMandays(totalMandays);
  if (!split) return '-';

  const value = stageKey === 'stage2' ? split.stage2Mandays : split.stage1Mandays;
  return String(value);
};

// Stage 1 and Stage 2 share ONE underlying audit schedule record (one Site Location
// list), so opening one stage for the first time must reuse whatever site list its
// sibling stage already has cached — never independently reseed an empty/default list.
// Doing so used to make the same site appear duplicated once both stages were saved,
// since each stage would otherwise seed its own near-identical copy.
export const resolveFreshStageSiteLocations = ({ schedulingDataByScope, baseKey, stageKey }) => {
  const siblingKey = stageKey === 'stage2' ? `${baseKey}:s1` : `${baseKey}:s2`;
  const siblingSites = schedulingDataByScope?.[siblingKey]?.siteLocations;
  return Array.isArray(siblingSites) && siblingSites.length > 0 ? siblingSites : [];
};
